// ============================================================================
// HAI Reporting Tool - Admin Dashboard Page
// Overview dashboard with statistics for administrators
// ============================================================================

import { useAuth } from '@/contexts/AuthContext';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useRealTimeMetrics } from '@/hooks/useRealTimeMetrics';
import { InfectionTrendChart } from '@/components/dashboard/InfectionTrendChart';
import { AlertCenter } from '@/components/dashboard/AlertCenter';
import { WardHealthCards } from '@/components/dashboard/WardHealthCard';
import { CareLevelInsights } from '@/components/dashboard/CareLevelInsights';

export function DashboardPage() {
  const { userProfile } = useAuth();
  const { metrics, loading: metricsLoading, error: metricsError } = useRealTimeMetrics();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="card">
          <h2 className="text-2xl font-bold mb-2">
            Welcome back, {userProfile?.displayName}!
          </h2>
          <p className="text-gray-600">
            Administrator Dashboard - View comprehensive HAI surveillance analytics and system overview
          </p>
        </div>

        {/* Metrics Error Display */}
        {metricsError && (
          <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
            <p className="font-semibold">Metrics Error:</p>
            <p className="text-sm">{metricsError}</p>
          </div>
        )}

        {/* Real-Time Statistics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-primary-50 border border-primary-200">
            <h3 className="text-sm font-medium text-primary-700 mb-1">
              Active Wards
            </h3>
            <p className="text-3xl font-bold text-primary-900">
              {metricsLoading ? '-' : metrics.wards}
            </p>
          </div>

          <div className="card bg-success-50 border border-success-200">
            <h3 className="text-sm font-medium text-success-700 mb-1">
              Data Submissions (7d)
            </h3>
            <p className="text-3xl font-bold text-success-900">
              {metricsLoading ? '-' : metrics.dataEntryComplete}
            </p>
            <p className="text-xs text-success-600 mt-1">Device snapshots</p>
          </div>

          <div className="card bg-info-50 border border-info-200">
            <h3 className="text-sm font-medium text-info-700 mb-1">
              Total Patients
            </h3>
            <p className="text-3xl font-bold text-info-900">
              {metricsLoading ? '-' : metrics.totalPatients}
            </p>
            <p className="text-xs text-info-600 mt-1">Past 7 days</p>
          </div>

          <div className="card bg-warning-50 border border-warning-200">
            <h3 className="text-sm font-medium text-warning-700 mb-1">
              Weekly HAI Cases
            </h3>
            <p className="text-3xl font-bold text-warning-900">
              {metricsLoading ? '-' : metrics.weeklyHAICases}
            </p>
            <p className="text-xs text-warning-600 mt-1">CAUTI, CLABSI, VAP</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/admin/data" className="btn btn-primary text-left">
              <div>
                <p className="font-medium">📋 View Data Submissions</p>
                <p className="text-sm opacity-90">See all submitted data</p>
              </div>
            </a>
            <a href="/admin/reports" className="btn btn-primary text-left">
              <div>
                <p className="font-medium">📈 Generate Reports</p>
                <p className="text-sm opacity-90">Export analytics and charts</p>
              </div>
            </a>
            <a href="/admin/settings" className="btn btn-primary text-left">
              <div>
                <p className="font-medium">⚙️ System Settings</p>
                <p className="text-sm opacity-90">Manage wards and users</p>
              </div>
            </a>
          </div>
        </div>

        {/* Infection Trend Chart */}
        <CareLevelInsights />

        {/* Infection Trend Chart */}
        <InfectionTrendChart />

        {/* Alert Center */}
        <AlertCenter />

        {/* Ward Health Cards */}
        <WardHealthCards />
      </div>
    </AdminLayout>
  );
}
